import { ResultsView } from '@kijiji/generated/graphql-types'
import { useMemo, useRef } from 'react'
import { useTheme } from 'styled-components'

import { HideAtOrLarger, ShowAtOrLarger } from '@/components/shared/breakpoint'
import { FiltersDrawer } from '@/components/srp/filters/FiltersDrawer/FiltersDrawer'
import { FILTER_CANONICAL } from '@/constants/search'
import { getExpandedFilterIdList } from '@/domain/srp/filters/getExpandedFilterIdList'
import { AllFiltersButton } from '@/features/filters/components/QuickFilters/AllFiltersButton'
import { QuickFilterButton } from '@/features/filters/components/QuickFilters/QuickFilterButton'
import { QuickFiltersLoading } from '@/features/filters/components/QuickFilters/QuickFiltersLoading'
import {
  QuickFiltersContainer,
  QuickFiltersSpacing,
  QuickFiltersWrapper,
} from '@/features/filters/components/QuickFilters/styled'
import { getQuickFilters } from '@/features/filters/utils/getQuickFilters'
import { useGetSrpState } from '@/features/search/hooks/useGetSrpState'
import { useGetSearchCategory } from '@/hooks/category/useGetSearchCategory'
import { useGetSearchResultsData } from '@/hooks/srp'
import { useFilterDrawerState } from '@/hooks/srp/useFilterDrawerState'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { trackEvent } from '@/lib/ga/utils/tracking'
import { Divider } from '@/ui/atoms/divider'

const { LOCATION } = FILTER_CANONICAL

export const QuickFilters = () => {
  const { spacing } = useTheme()
  const { category } = useGetSearchCategory()
  const { viewType } = useGetSrpState()
  const { data, loadingFilters } = useGetSearchResultsData()
  const { controls, searchQuery } = data ?? {}

  const selectedCategoryId = category.id
  const quickFiltersSorted = useMemo(
    () => getQuickFilters(controls?.filtering ?? [], selectedCategoryId),
    [controls?.filtering, selectedCategoryId]
  )

  const accordionContainerRef = useRef<HTMLDivElement>(null)
  const { setFilterDrawerState } = useFilterDrawerState()

  const resetScrollPosition = () => {
    accordionContainerRef.current?.scrollTo(0, 0)
  }

  const scrollToFilter = (id: string) => {
    const filterElement = accordionContainerRef.current?.querySelector(
      `[data-accordion-id="${id}"]`
    )
    if (filterElement) {
      filterElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  const handleClickAllFilters = () => {
    trackEvent({ action: GA_EVENT.QuickFilterClick, label: 'filter=all' })
    setFilterDrawerState({ isOpen: true, openItems: getExpandedFilterIdList(selectedCategoryId) })

    resetScrollPosition()
  }

  const handleClickQuickFilter = (id: string) => {
    // The category section is a special case, it uses the filter group name as its accordion id
    if (id === FILTER_CANONICAL.CATEGORY) id = FILTER_CANONICAL.CATEGORY_SECTION
    if (id === FILTER_CANONICAL.LOCATION) id = 'locationSection'

    trackEvent({ action: GA_EVENT.QuickFilterClick, label: `filter=${id}` })
    setFilterDrawerState({ isOpen: true, openItems: [id] })
    scrollToFilter(id)
  }

  const renderQuickFilterButtons = () => {
    /**
     * The location section should be excluded from the quick filters on
     */
    const hideOnDesktop: string[] = [LOCATION]

    return loadingFilters ? (
      <QuickFiltersLoading />
    ) : (
      quickFiltersSorted.map((filter) => {
        const handleClick = () => handleClickQuickFilter(filter.name)

        const shouldHideOnDesktop = hideOnDesktop.includes(filter.name)

        return (
          <QuickFilterButton
            hideOnDesktop={shouldHideOnDesktop}
            key={`quick-filter-${filter.name}`}
            filter={filter}
            onClick={handleClick}
            {...(filter.name === FILTER_CANONICAL.LOCATION ? { area: searchQuery?.area } : {})}
          />
        )
      })
    )
  }

  if (!quickFiltersSorted.length && !loadingFilters) return null

  return (
    <>
      <QuickFiltersContainer isMapView={viewType === ResultsView.Map}>
        <QuickFiltersSpacing>
          <QuickFiltersWrapper gap={spacing.defaultSmall}>
            <HideAtOrLarger breakpoint="medium">
              <AllFiltersButton variant="secondary" onClick={handleClickAllFilters} />
            </HideAtOrLarger>

            {renderQuickFilterButtons()}

            <ShowAtOrLarger breakpoint="medium">
              <AllFiltersButton onClick={handleClickAllFilters} />
            </ShowAtOrLarger>
          </QuickFiltersWrapper>
        </QuickFiltersSpacing>
      </QuickFiltersContainer>

      <ShowAtOrLarger breakpoint="medium">
        <Divider bottom={spacing.default} top={spacing.default} />
      </ShowAtOrLarger>

      {/* TODO: Remove this extra drawer. It should open the original filters modal we already have */}
      <FiltersDrawer accordionContainerRef={accordionContainerRef} />
    </>
  )
}
